import { Store, StorageType } from '@nakedplatform/support';
import { formatISO, differenceInSeconds } from 'date-fns';

export const Time = {
  PurchaseCompleted: 'PurchaseCompletedTime',
  QuoteCompleted: 'QuoteCompletedTime',
  QuoteStarted: 'QuoteStartedTime',
  FirstInteraction: 'FirstInteractionTime',
};

/**
 * Retrieves times relating to quote start
 */
function getQuoteStartedTime() {
  if (Store.hasStorage) {
    const storage = Store.getStorage(StorageType.LocalStorage);
    const startTime = storage.getItem(Time.QuoteStarted);
    const landingTime = storage.getItem(Time.FirstInteraction);
    const landingPageTime = new Date(landingTime);

    if (!startTime) {
      const quoteStartedTime = new Date();

      storage.setItem(Time.QuoteStarted, formatISO(quoteStartedTime));
      const timeToFirstStartedQuote = differenceInSeconds(quoteStartedTime, landingPageTime);

      return {
        firstStartedQuote: true,
        timeToFirstStartedQuote,
      };
    }

    const startDate = new Date(startTime);
    const timeToFirstStartedQuote = differenceInSeconds(startDate, landingPageTime);

    return {
      timeToFirstStartedQuote,
    };
  }

  return {};
}
/**
 * Retrieves times relating to quote completion
 */
function getQuoteCompletedTime() {
  if (Store.hasStorage) {
    const storage = Store.getStorage(StorageType.LocalStorage);
    const startTime = storage.getItem(Time.QuoteStarted);
    const startDate = new Date(startTime);
    const completedTime = storage.getItem(Time.QuoteCompleted);

    if (!completedTime) {
      const { timeToFirstStartedQuote } = getQuoteStartedTime();
      const quoteCompletedTime = new Date();

      storage.setItem(Time.QuoteCompleted, quoteCompletedTime.toISOString());
      const timeToFirstCompletedQuote = differenceInSeconds(quoteCompletedTime, startDate);

      return {
        firstCompletedQuote: true,
        timeToFirstStartedQuote,
        timeToFirstCompletedQuote,
      };
    }

    const completedDate = new Date(completedTime);
    const timeToFirstCompletedQuote = differenceInSeconds(completedDate, startDate);

    return {
      timeToFirstCompletedQuote,
    };
  }

  return {};
}
/**
 * Retrieves purchase times from storage
 */
function getPurchaseTime() {
  if (Store.hasStorage) {
    const storage = Store.getStorage(StorageType.LocalStorage);
    const purchaseTime = storage.getItem(Time.PurchaseCompleted);

    if (!purchaseTime) {
      const quoteCompletedTime = storage.getItem(Time.QuoteCompleted);
      const quoteCompletedDate = new Date(quoteCompletedTime);
      const purchaseCompletedTime = new Date();

      storage.setItem(Time.PurchaseCompleted, formatISO(purchaseCompletedTime));
      const timeToFirstCompletedPurchase = differenceInSeconds(
        purchaseCompletedTime,
        quoteCompletedDate
      );
      const firstCompletedPurchase = true;
      const { timeToFirstStartedQuote = 0 } = getQuoteStartedTime();
      const { timeToFirstCompletedQuote = 0 } = getQuoteCompletedTime();
      const totalTimeToFirstCompletedPurchase =
        timeToFirstCompletedPurchase + timeToFirstStartedQuote + timeToFirstCompletedQuote;

      return {
        totalTimeToFirstCompletedPurchase,
        timeToFirstCompletedPurchase,
        firstCompletedPurchase,
      };
    }
  }

  return {};
}
/**
 * Retrieves quote times from storage
 */
function getQuoteTime() {
  if (Store.hasStorage) {
    const storage = Store.getStorage(StorageType.LocalStorage);
    const startTime = storage.getItem(Time.QuoteStarted);
    const completedTime = storage.getItem(Time.QuoteCompleted);

    if (!completedTime) {
      const quoteCompletedTime = new Date();
      const { timeToFirstStartedQuote } = getQuoteStartedTime();

      storage.setItem(Time.QuoteCompleted, quoteCompletedTime.toISOString());
      const startDate = new Date(startTime);
      const timeToFirstCompletedQuote = differenceInSeconds(quoteCompletedTime, startDate);
      const totalTimeToFirstCompletedQuote = timeToFirstStartedQuote + timeToFirstCompletedQuote;

      return {
        timeToFirstCompletedQuote,
        timeToFirstStartedQuote,
        totalTimeToFirstCompletedQuote,
        firstCompletedQuote: false,
      };
    }

    const completedDate = new Date(completedTime);
    const startDate = new Date(startTime);
    const timeToFirstCompletedQuote = differenceInSeconds(completedDate, startDate);

    return { timeToFirstCompletedQuote };
  }

  return {};
}
/**
 * Retrieves Quote/Purchase times
 *
 * @param isQuote - Indicates whether
 */
export function getEventTime(isQuote: boolean) {
  return isQuote ? getQuoteTime() : getPurchaseTime();
}
